import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Trash2, Plus, Eye, EyeOff, Save, Users, CreditCard, Edit, Ban, Key, Coins, AlertTriangle, Upload, Image as ImageIcon, ExternalLink, Play, Mail, CheckCircle, Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth config with token from localStorage
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  };
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    preview_image: '',
    background_options: [],
    song_options: []
  });

  // Template Edit State
  const [editTemplateDialog, setEditTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const templateThumbnailRef = useRef(null);

  // Background Gallery State
  const [galleryImages, setGalleryImages] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [newGalleryImage, setNewGalleryImage] = useState({ name: '', category: 'general' });
  const galleryFileRef = useRef(null);

  // Song Gallery State
  const [gallerySongs, setGallerySongs] = useState([]);
  const [uploadingSong, setUploadingSong] = useState(false);
  const [newSong, setNewSong] = useState({ name: '', artist: '', category: 'general' });
  const songFileRef = useRef(null);

  // User Management State
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [deleteUserDialog, setDeleteUserDialog] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [addCreditsDialog, setAddCreditsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: '', email: '', phone: '', whatsapp: '', card_credits: 0, suspended: false, role: 'customer' });
  const [newPassword, setNewPassword] = useState('');
  const [creditsToAdd, setCreditsToAdd] = useState(1);
  const [deleteOption, setDeleteOption] = useState('delete'); // 'delete' or 'transfer'
  const [transferToUserId, setTransferToUserId] = useState('');
  const [processing, setProcessing] = useState(false);

  // Payment Settings State
  const [paymentSettings, setPaymentSettings] = useState({
    bayarcash_portal_key: '',
    bayarcash_pat: '',
    use_sandbox: true,
    pricing_packages: [],
    invoice_company_name: '',
    invoice_address: '',
    invoice_phone: '',
    invoice_email: ''
  });
  const [showCredentials, setShowCredentials] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Preview Cards State
  const [previewCards, setPreviewCards] = useState([]);
  const [previewCategories, setPreviewCategories] = useState([]);
  const [editPreviewDialog, setEditPreviewDialog] = useState(false);
  const [editingPreview, setEditingPreview] = useState(null);
  const [uploadingPreviewThumbnail, setUploadingPreviewThumbnail] = useState(false);
  const previewThumbnailRef = useRef(null);
  
  // Category Management State
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [uploadingCategoryThumbnail, setUploadingCategoryThumbnail] = useState(false);
  const categoryThumbnailRef = useRef(null);
  
  // Gallery Filter State
  const [galleryFilter, setGalleryFilter] = useState('all');
  const [editGalleryDialog, setEditGalleryDialog] = useState(false);
  const [editingGalleryImage, setEditingGalleryImage] = useState(null);
  
  // Song Edit State
  const [editSongDialog, setEditSongDialog] = useState(false);
  const [editingSong, setEditingSong] = useState(null);

  // Landing Settings State
  const [landingSettings, setLandingSettings] = useState({
    youtube_video_id: '',
    video_thumbnail: '',
    hero_title: '',
    hero_subtitle: ''
  });
  const [savingLanding, setSavingLanding] = useState(false);
  const [uploadingVideoThumbnail, setUploadingVideoThumbnail] = useState(false);
  const videoThumbnailRef = useRef(null);

  // Email Settings State
  const [emailSettings, setEmailSettings] = useState({
    resend_api_key: '',
    sender_email: '',
    sender_name: '',
    enable_immediate_notification: true,
    enable_followup_notification: true,
    enable_success_notification: true,
    followup_delay_hours: 2,
    immediate_subject: '',
    immediate_body: '',
    followup_subject: '',
    followup_body: '',
    success_subject: '',
    success_body: ''
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Site Settings State
  const [siteSettings, setSiteSettings] = useState({ maintenance_mode: false, maintenance_message: 'Laman web sedang dalam penyelenggaraan. Sila cuba sebentar lagi.' });
  const [savingSiteSettings, setSavingSiteSettings] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, cardsRes, usersRes, paymentsRes, settingsRes, galleryRes, songsRes, previewCardsRes, categoriesRes, landingRes, emailRes, siteRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/templates`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/admin/cards`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/admin/users`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/admin/payments`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/admin/payment-settings`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/background-gallery`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/song-gallery`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/preview-cards`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/preview-categories`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/admin/landing-settings`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/admin/email-settings`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/admin/site-settings`, getAuthConfig())
      ]);
      setTemplates(templatesRes.data);
      setCards(cardsRes.data);
      setUsers(usersRes.data || []);
      setPayments(paymentsRes.data || []);
      setGalleryImages(galleryRes.data || []);
      setGallerySongs(songsRes.data || []);
      setPreviewCards(previewCardsRes.data || []);
      setPreviewCategories(categoriesRes.data || []);
      setLandingSettings(landingRes.data || {});
      
      const settings = settingsRes.data;
      setPaymentSettings({
        bayarcash_portal_key: '',
        bayarcash_pat: '',
        use_sandbox: settings.use_sandbox ?? true,
        pricing_packages: settings.pricing_packages || [],
        invoice_company_name: settings.invoice_company_name || '',
        invoice_address: settings.invoice_address || '',
        invoice_phone: settings.invoice_phone || '',
        invoice_email: settings.invoice_email || '',
        bayarcash_portal_key_set: settings.bayarcash_portal_key_set,
        bayarcash_pat_set: settings.bayarcash_pat_set
      });

      // Set email settings
      const emailData = emailRes.data;
      setEmailSettings({
        resend_api_key: '',
        sender_email: emailData.sender_email || '',
        sender_name: emailData.sender_name || '',
        enable_immediate_notification: emailData.enable_immediate_notification ?? true,
        enable_followup_notification: emailData.enable_followup_notification ?? true,
        enable_success_notification: emailData.enable_success_notification ?? true,
        followup_delay_hours: emailData.followup_delay_hours || 2,
        immediate_subject: emailData.immediate_subject || '',
        immediate_body: emailData.immediate_body || '',
        followup_subject: emailData.followup_subject || '',
        followup_body: emailData.followup_body || '',
        success_subject: emailData.success_subject || '',
        success_body: emailData.success_body || '',
        resend_api_key_set: emailData.resend_api_key_set
      });

      // Set site settings
      setSiteSettings({
        maintenance_mode: siteRes.data.maintenance_mode ?? false,
        maintenance_message: siteRes.data.maintenance_message || 'Laman web sedang dalam penyelenggaraan. Sila cuba sebentar lagi.'
      });
    } catch (error) {
      toast.error('Gagal memuatkan data');
    } finally {
      setLoading(false);
    }
  };

  // Template Thumbnail Upload
  const handleThumbnailUpload = async (e, isEdit = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Sila pilih fail imej');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Saiz fail melebihi 2MB');
      return;
    }
    
    setUploadingThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/template-thumbnail`,
        formData,
        { ...getAuthConfig(), headers: { ...getAuthConfig().headers, 'Content-Type': 'multipart/form-data' } }
      );
      
      if (isEdit && editingTemplate) {
        setEditingTemplate({ ...editingTemplate, preview_image: response.data.url });
      } else {
        setNewTemplate({ ...newTemplate, preview_image: response.data.url });
      }
      toast.success('Thumbnail berjaya dimuat naik');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat naik thumbnail');
    } finally {
      setUploadingThumbnail(false);
      e.target.value = '';
    }
  };

  // User Management Functions
  const openEditUser = (user) => {
    setSelectedUser(user);
    setEditUserForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      whatsapp: user.whatsapp || '',
      card_credits: user.card_credits || 0,
      suspended: user.suspended || false,
      role: user.role || 'customer'
    });
    setEditUserDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    setProcessing(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${selectedUser.user_id}`,
        editUserForm,
        getAuthConfig()
      );
      toast.success('Pengguna berjaya dikemaskini');
      setEditUserDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengemaskini pengguna');
    } finally {
      setProcessing(false);
    }
  };

  const openDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteOption('delete');
    setTransferToUserId('');
    setDeleteUserDialog(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setProcessing(true);
    try {
      const params = new URLSearchParams();
      if (deleteOption === 'delete') {
        params.append('delete_cards', 'true');
      } else if (deleteOption === 'transfer' && transferToUserId) {
        params.append('transfer_to', transferToUserId);
      }
      
      await axios.delete(
        `${BACKEND_URL}/api/admin/users/${selectedUser.user_id}?${params.toString()}`,
        getAuthConfig()
      );
      toast.success('Pengguna berjaya dipadamkan');
      setDeleteUserDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memadamkan pengguna');
    } finally {
      setProcessing(false);
    }
  };

  const openResetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setResetPasswordDialog(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Kata laluan mestilah sekurang-kurangnya 6 aksara');
      return;
    }
    setProcessing(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${selectedUser.user_id}/reset-password`,
        { new_password: newPassword },
        getAuthConfig()
      );
      toast.success('Kata laluan berjaya ditetapkan semula');
      setResetPasswordDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menetapkan semula kata laluan');
    } finally {
      setProcessing(false);
    }
  };

  const openAddCredits = (user) => {
    setSelectedUser(user);
    setCreditsToAdd(1);
    setAddCreditsDialog(true);
  };

  const handleAddCredits = async () => {
    if (!selectedUser) return;
    setProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${selectedUser.user_id}/add-credits`,
        { credits: creditsToAdd },
        getAuthConfig()
      );
      toast.success(`${creditsToAdd} kredit berjaya ditambah`);
      setAddCreditsDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambah kredit');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleSuspend = async (user) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${user.user_id}`,
        { suspended: !user.suspended },
        getAuthConfig()
      );
      toast.success(user.suspended ? 'Pengguna diaktifkan semula' : 'Pengguna digantung');
      fetchData();
    } catch (error) {
      toast.error('Gagal mengemas kini status pengguna');
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name || !newTemplate.description || !newTemplate.preview_image) {
      toast.error('Sila lengkapkan semua maklumat');
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/templates`,
        {
          ...newTemplate,
          background_options: newTemplate.background_options.filter(url => url.trim() !== '')
        },
        getAuthConfig()
      );
      toast.success('Templat berjaya dicipta!');
      setCreateDialogOpen(false);
      setNewTemplate({ name: '', description: '', preview_image: '', background_options: [''] });
      fetchData();
    } catch (error) {
      toast.error('Gagal mencipta templat');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Adakah anda pasti mahu memadamkan templat ini?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/templates/${templateId}`, getAuthConfig());
      toast.success('Templat berjaya dipadamkan');
      fetchData();
    } catch (error) {
      toast.error('Gagal memadamkan templat');
    }
  };

  // Template Edit Functions
  const openEditTemplate = (template) => {
    setEditingTemplate({
      ...template,
      background_options: template.background_options || [],
      song_options: template.song_options || []
    });
    setEditTemplateDialog(true);
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    setProcessing(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/templates/${editingTemplate.template_id}`,
        {
          name: editingTemplate.name,
          description: editingTemplate.description,
          preview_image: editingTemplate.preview_image,
          background_options: editingTemplate.background_options || [],
          song_options: editingTemplate.song_options || []
        },
        getAuthConfig()
      );
      toast.success('Templat berjaya dikemaskini!');
      setEditTemplateDialog(false);
      setEditingTemplate(null);
      fetchData();
    } catch (error) {
      toast.error('Gagal mengemaskini templat');
    } finally {
      setProcessing(false);
    }
  };

  // Background Gallery Functions
  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Saiz fail melebihi 2MB. Sila muat naik imej yang lebih kecil.');
      return;
    }

    setUploadingGallery(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', newGalleryImage.name || file.name);
      formData.append('category', newGalleryImage.category || 'general');

      await axios.post(
        `${BACKEND_URL}/api/admin/background-gallery`,
        formData,
        { 
          ...getAuthConfig(),
          headers: { ...getAuthConfig().headers, 'Content-Type': 'multipart/form-data' }
        }
      );
      toast.success('Imej berjaya dimuat naik!');
      setNewGalleryImage({ name: '', category: 'general' });
      if (galleryFileRef.current) galleryFileRef.current.value = '';
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat naik imej');
    } finally {
      setUploadingGallery(false);
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (!window.confirm('Adakah anda pasti mahu memadamkan imej ini?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/background-gallery/${imageId}`, getAuthConfig());
      toast.success('Imej berjaya dipadamkan');
      fetchData();
    } catch (error) {
      toast.error('Gagal memadamkan imej');
    }
  };

  // Song Gallery Functions
  const handleSongUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Saiz fail melebihi 10MB. Sila muat naik fail audio yang lebih kecil.');
      return;
    }

    setUploadingSong(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', newSong.name || file.name);
      formData.append('artist', newSong.artist || '');
      formData.append('category', newSong.category || 'general');

      await axios.post(
        `${BACKEND_URL}/api/admin/song-gallery`,
        formData,
        { 
          ...getAuthConfig(),
          headers: { ...getAuthConfig().headers, 'Content-Type': 'multipart/form-data' }
        }
      );
      toast.success('Lagu berjaya dimuat naik!');
      setNewSong({ name: '', artist: '', category: 'general' });
      if (songFileRef.current) songFileRef.current.value = '';
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat naik lagu');
    } finally {
      setUploadingSong(false);
    }
  };

  const handleDeleteSong = async (songId) => {
    if (!window.confirm('Adakah anda pasti mahu memadamkan lagu ini?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/song-gallery/${songId}`, getAuthConfig());
      toast.success('Lagu berjaya dipadamkan');
      fetchData();
    } catch (error) {
      toast.error('Gagal memadamkan lagu');
    }
  };

  const handleSavePaymentSettings = async () => {
    setSavingSettings(true);
    try {
      const updateData = {
        use_sandbox: paymentSettings.use_sandbox,
        pricing_packages: paymentSettings.pricing_packages,
        invoice_company_name: paymentSettings.invoice_company_name,
        invoice_address: paymentSettings.invoice_address,
        invoice_phone: paymentSettings.invoice_phone,
        invoice_email: paymentSettings.invoice_email
      };

      if (paymentSettings.bayarcash_portal_key) {
        updateData.bayarcash_portal_key = paymentSettings.bayarcash_portal_key;
      }
      if (paymentSettings.bayarcash_pat) {
        updateData.bayarcash_pat = paymentSettings.bayarcash_pat;
      }

      await axios.put(
        `${BACKEND_URL}/api/admin/payment-settings`,
        updateData,
        getAuthConfig()
      );
      toast.success('Tetapan pembayaran berjaya disimpan');
      fetchData();
    } catch (error) {
      toast.error('Gagal menyimpan tetapan');
    } finally {
      setSavingSettings(false);
    }
  };

  const updatePricingPackage = (index, field, value) => {
    const updated = [...paymentSettings.pricing_packages];
    updated[index] = { ...updated[index], [field]: field === 'price' || field === 'quantity' ? Number(value) : value };
    setPaymentSettings({ ...paymentSettings, pricing_packages: updated });
  };

  const addPricingPackage = () => {
    setPaymentSettings({
      ...paymentSettings,
      pricing_packages: [...paymentSettings.pricing_packages, { quantity: 1, price: 49, label: '1 Kad' }]
    });
  };

  const removePricingPackage = (index) => {
    const updated = paymentSettings.pricing_packages.filter((_, i) => i !== index);
    setPaymentSettings({ ...paymentSettings, pricing_packages: updated });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getUserCardCount = (userId) => {
    return cards.filter(c => c.user_id === userId).length;
  };

  // Preview Cards Management Functions
  const openEditPreview = (card) => {
    setEditingPreview({ ...card });
    setEditPreviewDialog(true);
  };

  const handlePreviewThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingPreview) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Sila pilih fail imej');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Saiz fail melebihi 2MB');
      return;
    }
    
    setUploadingPreviewThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await axios.post(
        `${BACKEND_URL}/api/admin/template-thumbnail`,
        formData,
        { ...getAuthConfig(), headers: { ...getAuthConfig().headers, 'Content-Type': 'multipart/form-data' } }
      );
      
      setEditingPreview({ ...editingPreview, preview_thumbnail: uploadRes.data.url });
      toast.success('Thumbnail berjaya dimuat naik');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat naik thumbnail');
    } finally {
      setUploadingPreviewThumbnail(false);
      e.target.value = '';
    }
  };

  const handleSavePreview = async () => {
    if (!editingPreview) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/cards/${editingPreview.card_id}/set-preview`,
        { 
          is_preview: true, 
          preview_thumbnail: editingPreview.preview_thumbnail,
          preview_category: editingPreview.preview_category 
        },
        getAuthConfig()
      );
      toast.success('Preview berjaya dikemaskini');
      setEditPreviewDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengemaskini preview');
    }
  };

  const handleCancelPreview = async (cardId) => {
    if (!window.confirm('Adakah anda pasti mahu membatalkan preview kad ini?')) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/cards/${cardId}/set-preview`,
        { is_preview: false },
        getAuthConfig()
      );
      toast.success('Preview kad berjaya dibatalkan');
      setEditPreviewDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membatalkan preview');
    }
  };

  // Category Management Functions
  const openCategoryDialog = (category = null) => {
    setEditingCategory(category);
    setNewCategoryName(category?.name || '');
    setCategoryDialog(true);
  };

  const handleCategoryThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingCategoryThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await axios.post(
        `${BACKEND_URL}/api/admin/template-thumbnail`,
        formData,
        { ...getAuthConfig(), headers: { ...getAuthConfig().headers, 'Content-Type': 'multipart/form-data' } }
      );
      
      setEditingCategory({ ...editingCategory, thumbnail: uploadRes.data.url });
      toast.success('Thumbnail berjaya dimuat naik');
    } catch (error) {
      toast.error('Gagal memuat naik thumbnail');
    } finally {
      setUploadingCategoryThumbnail(false);
      e.target.value = '';
    }
  };

  const handleSaveCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Sila masukkan nama kategori');
      return;
    }
    
    try {
      if (editingCategory?.category_id) {
        await axios.put(
          `${BACKEND_URL}/api/admin/preview-categories/${editingCategory.category_id}`,
          { name: newCategoryName, thumbnail: editingCategory.thumbnail },
          getAuthConfig()
        );
        toast.success('Kategori berjaya dikemaskini');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/preview-categories`,
          { name: newCategoryName, thumbnail: editingCategory?.thumbnail },
          getAuthConfig()
        );
        toast.success('Kategori berjaya ditambah');
      }
      setCategoryDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan kategori');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Adakah anda pasti mahu memadamkan kategori ini?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/preview-categories/${categoryId}`, getAuthConfig());
      toast.success('Kategori berjaya dipadamkan');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memadamkan kategori');
    }
  };

  // Gallery Image Edit Functions
  const openEditGalleryImage = (image) => {
    setEditingGalleryImage({ ...image });
    setEditGalleryDialog(true);
  };

  const handleSaveGalleryImage = async () => {
    if (!editingGalleryImage) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/background-gallery/${editingGalleryImage.image_id}`,
        null,
        { ...getAuthConfig(), params: { name: editingGalleryImage.name, category: editingGalleryImage.category } }
      );
      toast.success('Imej berjaya dikemaskini');
      setEditGalleryDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengemaskini imej');
    }
  };

  // Song Edit Functions
  const openEditSong = (song) => {
    setEditingSong({ ...song });
    setEditSongDialog(true);
  };

  const handleSaveSong = async () => {
    if (!editingSong) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/song-gallery/${editingSong.song_id}`,
        null,
        { ...getAuthConfig(), params: { name: editingSong.name, artist: editingSong.artist, category: editingSong.category } }
      );
      toast.success('Lagu berjaya dikemaskini');
      setEditSongDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengemaskini lagu');
    }
  };

  // Save Landing Settings
  const handleSaveLandingSettings = async () => {
    setSavingLanding(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/landing-settings`,
        landingSettings,
        getAuthConfig()
      );
      toast.success('Tetapan landing page berjaya disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan tetapan');
    } finally {
      setSavingLanding(false);
    }
  };

  // Upload Video Thumbnail
  const handleVideoThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingVideoThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const uploadRes = await axios.post(
        `${BACKEND_URL}/api/admin/template-thumbnail`,
        formData,
        { ...getAuthConfig(), headers: { ...getAuthConfig().headers, 'Content-Type': 'multipart/form-data' } }
      );

      setLandingSettings({ ...landingSettings, video_thumbnail: uploadRes.data.url });
      toast.success('Thumbnail video berjaya dimuat naik');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat naik thumbnail');
    } finally {
      setUploadingVideoThumbnail(false);
    }
  };

  // Save Email Settings
  const handleSaveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      const updateData = {
        sender_email: emailSettings.sender_email,
        sender_name: emailSettings.sender_name,
        enable_immediate_notification: emailSettings.enable_immediate_notification,
        enable_followup_notification: emailSettings.enable_followup_notification,
        enable_success_notification: emailSettings.enable_success_notification,
        followup_delay_hours: emailSettings.followup_delay_hours,
        immediate_subject: emailSettings.immediate_subject,
        immediate_body: emailSettings.immediate_body,
        followup_subject: emailSettings.followup_subject,
        followup_body: emailSettings.followup_body,
        success_subject: emailSettings.success_subject,
        success_body: emailSettings.success_body
      };
      
      // Only include API key if it was changed
      if (emailSettings.resend_api_key) {
        updateData.resend_api_key = emailSettings.resend_api_key;
      }
      
      await axios.put(
        `${BACKEND_URL}/api/admin/email-settings`,
        updateData,
        getAuthConfig()
      );
      toast.success('Tetapan email berjaya disimpan');
      // Clear the API key field after save
      setEmailSettings(prev => ({ ...prev, resend_api_key: '', resend_api_key_set: true }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan tetapan email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSaveSiteSettings = async () => {
    setSavingSiteSettings(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/site-settings`,
        siteSettings,
        getAuthConfig()
      );
      toast.success('Tetapan laman berjaya disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan tetapan laman');
    } finally {
      setSavingSiteSettings(false);
    }
  };

  // Send Test Email
  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/email-settings/test`,
        {},
        getAuthConfig()
      );
      toast.success(res.data.message || 'Test email berjaya dihantar!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghantar test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Get category name helper
  const getCategoryName = (categoryId) => {
    const cat = previewCategories.find(c => c.category_id === categoryId);
    return cat?.name || 'Tiada Kategori';
  };

  // Filter gallery images
  const filteredGalleryImages = galleryFilter === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === galleryFilter);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D348B] mx-auto mb-4"></div>
          <p className="text-gray-600">Memuatkan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Panel Admin | Kawkad</title>
      </Helmet>
      <nav className="bg-[#3D348B] sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-white">Panel Admin</h1>
          <Button
            data-testid="back-to-dashboard-btn"
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="border-[#F7B801] text-[#F7B801] hover:bg-[#F7B801] hover:text-[#3D348B] rounded-full"
          >
            Kembali ke Dashboard
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="templates" className="space-y-8">
          <TabsList className="bg-white border border-gray-100">
            <TabsTrigger value="templates" data-testid="templates-tab">Templat</TabsTrigger>
            <TabsTrigger value="previews" data-testid="previews-tab">Senarai Preview</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="gallery-tab">Galeri Background</TabsTrigger>
            <TabsTrigger value="songs" data-testid="songs-tab">Galeri Lagu</TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab">Kad</TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">Pengguna</TabsTrigger>
            <TabsTrigger value="payments" data-testid="payments-tab">Pembayaran</TabsTrigger>
            <TabsTrigger value="landing" data-testid="landing-tab">Landing Setup</TabsTrigger>
            <TabsTrigger value="email" data-testid="email-tab">Email/Notifikasi</TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">Tetapan</TabsTrigger>
          </TabsList>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-playfair font-bold text-gray-900">Templat</h2>
                <p className="text-gray-600 mt-1">Urus templat kad jemputan</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <div key={template.template_id} className="bg-white rounded-xl overflow-hidden card-shadow">
                  {template.preview_image ? (
                    <div className="w-full h-64 bg-gray-100 flex items-center justify-center">
                      <img src={template.preview_image} alt={template.name} className="h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-full h-64 bg-gray-100 flex items-center justify-center text-gray-400">
                      <ImageIcon className="w-12 h-12" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-playfair font-semibold text-xl text-gray-900">{template.name}</h3>
                      {template.is_card_template && (
                        <span className="px-2 py-0.5 bg-[#7678ED]/10 text-[#7678ED] text-xs rounded-full">Dari Kad</span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm mt-2">{template.description}</p>
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditTemplate(template)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteTemplate(template.template_id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Padam
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Preview Cards Tab */}
          <TabsContent value="previews" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-playfair font-bold text-gray-900">Senarai Preview</h2>
                <p className="text-gray-600 mt-1">Urus preview kad jemputan yang dipaparkan di landing page</p>
              </div>
            </div>

            {/* Category Management Section */}
            <div className="bg-white rounded-xl p-6 card-shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg">Kategori Preview</h3>
                <Button size="sm" onClick={() => openCategoryDialog()}>
                  <Plus className="w-4 h-4 mr-1" /> Tambah Kategori
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {previewCategories.map((cat) => (
                  <div key={cat.category_id} className="border rounded-lg p-3 relative group">
                    {cat.thumbnail && (
                      <div className="aspect-video bg-gray-100 rounded mb-2 overflow-hidden">
                        <img src={cat.thumbnail} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <p className="font-medium text-sm truncate">{cat.name}</p>
                    <p className="text-xs text-gray-500">{previewCards.filter(c => c.preview_category === cat.category_id).length} kad</p>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => openCategoryDialog(cat)}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="destructive" className="h-7 w-7 p-0" onClick={() => handleDeleteCategory(cat.category_id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {previewCards.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center card-shadow">
                <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Tiada Preview Kad</h3>
                <p className="text-gray-600 mb-4">
                  Anda boleh menambah kad sebagai preview melalui Editor Kad dengan menekan butang "Jadikan Preview"
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {previewCards.map((card) => (
                  <div key={card.card_id} className="bg-white rounded-xl overflow-hidden card-shadow group">
                    <div className="relative aspect-[9/16] bg-gray-100">
                      <img
                        src={card.preview_thumbnail || card.background_thumbnail || card.background_image}
                        alt={`${card.bride_name} & ${card.groom_name}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 left-3 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        LIVE
                      </div>
                      {card.preview_category && (
                        <div className="absolute top-3 right-3 bg-[#3D348B] text-white text-xs px-2 py-1 rounded-full">
                          {getCategoryName(card.preview_category)}
                        </div>
                      )}
                    </div>
                    <div className="p-4 space-y-2">
                      <h3 className="font-semibold text-gray-900 truncate">{card.bride_name} & {card.groom_name}</h3>
                      <p className="text-sm text-gray-500">{card.wedding_date}</p>
                      <a 
                        href={`${window.location.origin}/${card.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#3D348B] hover:underline block truncate"
                      >
                        <ExternalLink className="w-3 h-3 inline mr-1" />
                        /{card.slug}
                      </a>
                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditPreview(card)}
                          className="flex-1"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleCancelPreview(card.card_id)}
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Edit Preview Dialog */}
          <Dialog open={editPreviewDialog} onOpenChange={setEditPreviewDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Edit Preview Kad</DialogTitle>
                <DialogDescription>Kemaskini thumbnail dan maklumat preview kad</DialogDescription>
              </DialogHeader>
              
              {editingPreview && (
                <div className="space-y-4">
                  {/* Thumbnail Preview & Upload */}
                  <div className="space-y-2">
                    <Label>Thumbnail Preview</Label>
                    <div className="flex gap-4 items-start">
                      <div className="w-32 aspect-[9/16] bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={editingPreview.preview_thumbnail || editingPreview.background_thumbnail || editingPreview.background_image}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 space-y-2">
                        <input
                          type="file"
                          ref={previewThumbnailRef}
                          onChange={handlePreviewThumbnailUpload}
                          accept="image/*"
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => previewThumbnailRef.current?.click()}
                          disabled={uploadingPreviewThumbnail}
                          className="w-full"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingPreviewThumbnail ? 'Memuat naik...' : 'Tukar Thumbnail'}
                        </Button>
                        <p className="text-xs text-gray-500">Saiz maksimum: 2MB. Format: JPG, PNG, WEBP</p>
                      </div>
                    </div>
                  </div>

                  {/* Category Selector */}
                  <div className="space-y-2">
                    <Label>Kategori</Label>
                    <Select
                      value={editingPreview.preview_category || 'none'}
                      onValueChange={(val) => setEditingPreview({ ...editingPreview, preview_category: val === 'none' ? null : val })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pilih kategori" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Tiada Kategori</SelectItem>
                        {previewCategories.map((cat) => (
                          <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Card Info (Read Only) */}
                  <div className="space-y-2">
                    <Label>Maklumat Kad</Label>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                      <p><span className="text-gray-500">Pasangan:</span> {editingPreview.bride_name} & {editingPreview.groom_name}</p>
                      <p><span className="text-gray-500">Tarikh:</span> {editingPreview.wedding_date}</p>
                      <p>
                        <span className="text-gray-500">Link:</span>{' '}
                        <a 
                          href={`${window.location.origin}/${editingPreview.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#3D348B] hover:underline"
                        >
                          {window.location.origin}/{editingPreview.slug}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button
                  variant="destructive"
                  onClick={() => editingPreview && handleCancelPreview(editingPreview.card_id)}
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Batal Preview
                </Button>
                <Button onClick={handleSavePreview}>
                  <Save className="w-4 h-4 mr-2" />
                  Simpan
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Category Edit Dialog */}
          <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingCategory?.category_id ? 'Edit Kategori' : 'Tambah Kategori Baharu'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Nama Kategori</Label>
                  <Input
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Contoh: Floral Elegance"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Thumbnail Kategori (Optional)</Label>
                  <div className="flex gap-3 items-center">
                    {editingCategory?.thumbnail && (
                      <div className="w-20 h-14 bg-gray-100 rounded overflow-hidden">
                        <img src={editingCategory.thumbnail} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <input
                      type="file"
                      ref={categoryThumbnailRef}
                      onChange={handleCategoryThumbnailUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => categoryThumbnailRef.current?.click()}
                      disabled={uploadingCategoryThumbnail}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      {uploadingCategoryThumbnail ? 'Memuat naik...' : 'Pilih Imej'}
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCategoryDialog(false)}>Batal</Button>
                <Button onClick={handleSaveCategory}>Simpan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Background Gallery Tab */}
          <TabsContent value="gallery" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-playfair font-bold text-gray-900">Galeri Background</h2>
                <p className="text-gray-600 mt-1">Urus koleksi imej latar belakang untuk pengguna</p>
              </div>
            </div>

            {/* Upload Section */}
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="font-semibold text-lg mb-4">Muat Naik Imej Baharu</h3>
              <p className="text-sm text-gray-500 mb-4">Saiz maksimum: 2MB. Format: JPG, PNG, WEBP</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Nama Imej</Label>
                  <Input
                    value={newGalleryImage.name}
                    onChange={(e) => setNewGalleryImage({ ...newGalleryImage, name: e.target.value })}
                    placeholder="Bunga Sakura"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kategori</Label>
                  <Select
                    value={newGalleryImage.category}
                    onValueChange={(val) => setNewGalleryImage({ ...newGalleryImage, category: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">Umum</SelectItem>
                      <SelectItem value="floral">Bunga</SelectItem>
                      <SelectItem value="nature">Alam Semula Jadi</SelectItem>
                      <SelectItem value="abstract">Abstrak</SelectItem>
                      <SelectItem value="traditional">Tradisional</SelectItem>
                      <SelectItem value="modern">Moden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Pilih Fail</Label>
                  <div className="flex gap-2">
                    <input
                      ref={galleryFileRef}
                      type="file"
                      accept="image/*"
                      onChange={handleGalleryUpload}
                      className="hidden"
                    />
                    <Button
                      onClick={() => galleryFileRef.current?.click()}
                      disabled={uploadingGallery}
                      className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED]"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingGallery ? 'Memuat naik...' : 'Pilih & Muat Naik'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter Section */}
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant={galleryFilter === 'all' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setGalleryFilter('all')}
                className={galleryFilter === 'all' ? 'bg-[#3D348B]' : ''}
              >
                Semua ({galleryImages.length})
              </Button>
              {['general', 'floral', 'nature', 'abstract', 'traditional', 'modern'].map((cat) => {
                const count = galleryImages.filter(img => img.category === cat).length;
                const labels = { general: 'Umum', floral: 'Bunga', nature: 'Alam', abstract: 'Abstrak', traditional: 'Tradisional', modern: 'Moden' };
                return (
                  <Button 
                    key={cat}
                    variant={galleryFilter === cat ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setGalleryFilter(cat)}
                    className={galleryFilter === cat ? 'bg-[#3D348B]' : ''}
                  >
                    {labels[cat]} ({count})
                  </Button>
                );
              })}
            </div>

            {/* Gallery Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredGalleryImages.map((img) => (
                <div key={img.image_id} className="bg-white rounded-lg overflow-hidden card-shadow group relative">
                  <img 
                    src={img.thumbnail || img.url} 
                    alt={img.name} 
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-2">
                    <p className="text-xs font-medium truncate">{img.name}</p>
                    <p className="text-[10px] text-gray-400 capitalize">{img.category}</p>
                  </div>
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditGalleryImage(img)}
                      className="bg-white hover:bg-gray-100"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteGalleryImage(img.image_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {filteredGalleryImages.length === 0 && (
                <div className="col-span-full text-center py-12 text-gray-400">
                  <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Tiada imej dalam kategori ini</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Edit Gallery Image Dialog */}
          <Dialog open={editGalleryDialog} onOpenChange={setEditGalleryDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Edit Imej</DialogTitle>
              </DialogHeader>
              {editingGalleryImage && (
                <div className="space-y-4">
                  <div className="w-full h-40 bg-gray-100 rounded-lg overflow-hidden">
                    <img src={editingGalleryImage.thumbnail || editingGalleryImage.url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="space-y-2">
                    <Label>Nama Imej</Label>
                    <Input
                      value={editingGalleryImage.name}
                      onChange={(e) => setEditingGalleryImage({ ...editingGalleryImage, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kategori</Label>
                    <Select
                      value={editingGalleryImage.category}
                      onValueChange={(val) => setEditingGalleryImage({ ...editingGalleryImage, category: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">Umum</SelectItem>
                        <SelectItem value="floral">Bunga</SelectItem>
                        <SelectItem value="nature">Alam Semula Jadi</SelectItem>
                        <SelectItem value="abstract">Abstrak</SelectItem>
                        <SelectItem value="traditional">Tradisional</SelectItem>
                        <SelectItem value="modern">Moden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditGalleryDialog(false)}>Batal</Button>
                <Button onClick={handleSaveGalleryImage}>Simpan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Song Gallery Tab */}
          <TabsContent value="songs" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-playfair font-bold text-gray-900">Galeri Lagu</h2>
                <p className="text-gray-600 mt-1">Urus koleksi lagu untuk kad jemputan</p>
              </div>
            </div>

            {/* Upload Section */}
            <div className="bg-white rounded-xl p-6 card-shadow">
              <h3 className="font-semibold text-lg mb-4">Muat Naik Lagu Baharu</h3>
              <p className="text-sm text-gray-500 mb-4">Saiz maksimum: 10MB. Format: MP3, WAV, OGG, M4A, AAC</p>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Nama Lagu</Label>
                  <Input
                    value={newSong.name}
                    onChange={(e) => setNewSong({ ...newSong, name: e.target.value })}
                    placeholder="Perfect - Ed Sheeran"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Artis</Label>
                  <Input
                    value={newSong.artist}
                    onChange={(e) => setNewSong({ ...newSong, artist: e.target.value })}
                    placeholder="Ed Sheeran"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kategori</Label>
                  <Select
                    value={newSong.category}
                    onValueChange={(val) => setNewSong({ ...newSong, category: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">Umum</SelectItem>
                      <SelectItem value="nasyid">Nasyid</SelectItem>
                      <SelectItem value="pop">Pop</SelectItem>
                      <SelectItem value="instrumental">Instrumental</SelectItem>
                      <SelectItem value="klasik">Klasik</SelectItem>
                      <SelectItem value="traditional">Tradisional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Pilih Fail Audio</Label>
                  <div className="flex gap-2">
                    <input
                      ref={songFileRef}
                      type="file"
                      accept="audio/*"
                      onChange={handleSongUpload}
                      className="hidden"
                    />
                    <Button
                      onClick={() => songFileRef.current?.click()}
                      disabled={uploadingSong}
                      className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED]"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingSong ? 'Memuat naik...' : 'Pilih & Muat Naik'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Songs List */}
            <div className="bg-white rounded-xl overflow-hidden card-shadow">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lagu</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Artis</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preview</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {gallerySongs.map((song) => (
                    <tr key={song.song_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{song.name}</td>
                      <td className="px-4 py-3 text-gray-600">{song.artist || '-'}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full capitalize">{song.category}</span>
                      </td>
                      <td className="px-4 py-3">
                        <audio controls className="h-8 w-40">
                          <source src={song.url} />
                        </audio>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditSong(song)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteSong(song.song_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {gallerySongs.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                        Tiada lagu dalam galeri. Muat naik lagu pertama anda di atas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Edit Song Dialog */}
          <Dialog open={editSongDialog} onOpenChange={setEditSongDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Edit Lagu</DialogTitle>
              </DialogHeader>
              {editingSong && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nama Lagu</Label>
                    <Input
                      value={editingSong.name}
                      onChange={(e) => setEditingSong({ ...editingSong, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nama Artis</Label>
                    <Input
                      value={editingSong.artist || ''}
                      onChange={(e) => setEditingSong({ ...editingSong, artist: e.target.value })}
                      placeholder="Contoh: Ed Sheeran"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kategori</Label>
                    <Select
                      value={editingSong.category}
                      onValueChange={(val) => setEditingSong({ ...editingSong, category: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">Umum</SelectItem>
                        <SelectItem value="nasyid">Nasyid</SelectItem>
                        <SelectItem value="pop">Pop</SelectItem>
                        <SelectItem value="instrumental">Instrumental</SelectItem>
                        <SelectItem value="klasik">Klasik</SelectItem>
                        <SelectItem value="traditional">Tradisional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="pt-2">
                    <Label className="text-gray-500 text-sm">Preview Audio</Label>
                    <audio controls className="w-full mt-2">
                      <source src={editingSong.url} />
                    </audio>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditSongDialog(false)}>Batal</Button>
                <Button onClick={handleSaveSong}>Simpan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Cards Tab */}
          <TabsContent value="orders" className="space-y-6">
            <div>
              <h2 className="text-3xl font-playfair font-bold text-gray-900">Semua Kad</h2>
              <p className="text-gray-600 mt-1">Senarai semua kad yang dicipta</p>
            </div>

            <div className="bg-white rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tajuk</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarikh</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {cards.map((card) => (
                    <tr key={card.card_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium">{card.bride_name} & {card.groom_name}</p>
                          <p className="text-sm text-gray-500">{card.title}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">/{card.slug}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          card.status === 'published' ? 'bg-[#7678ED]/20 text-[#3D348B]' : 'bg-[#F7B801]/20 text-[#F18701]'
                        }`}>
                          {card.status === 'published' ? 'Diterbitkan' : 'Draf'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(card.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Users Tab - Enhanced with management features */}
          <TabsContent value="users" className="space-y-6">
            <div>
              <h2 className="text-3xl font-playfair font-bold text-gray-900">Pengguna</h2>
              <p className="text-gray-600 mt-1">Urus semua pengguna berdaftar</p>
            </div>

            <div className="bg-white rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nama</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kad</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kredit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tindakan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.user_id} className={`hover:bg-gray-50 ${user.suspended ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{user.name}</span>
                          {user.role === 'admin' && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Admin</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm">{user.email}</td>
                      <td className="px-4 py-4 text-sm">{getUserCardCount(user.user_id)}</td>
                      <td className="px-4 py-4">
                        <span className="font-semibold text-[#7678ED]">{user.card_credits || 0}</span>
                      </td>
                      <td className="px-4 py-4">
                        {user.suspended ? (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Digantung</span>
                        ) : (
                          <span className="px-2 py-1 bg-[#7678ED]/20 text-[#3D348B] text-xs rounded-full">Aktif</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditUser(user)} title="Edit">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openAddCredits(user)} title="Tambah Kredit">
                            <Coins className="w-4 h-4 text-[#F7B801]" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openResetPassword(user)} title="Reset Password">
                            <Key className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleToggleSuspend(user)} 
                            title={user.suspended ? "Aktifkan" : "Gantung"}
                          >
                            <Ban className={`w-4 h-4 ${user.suspended ? 'text-[#3D348B]' : 'text-orange-600'}`} />
                          </Button>
                          {user.role !== 'admin' && (
                            <Button variant="ghost" size="sm" onClick={() => openDeleteUser(user)} title="Padam">
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-6">
            <div>
              <h2 className="text-3xl font-playfair font-bold text-gray-900">Pembayaran</h2>
              <p className="text-gray-600 mt-1">Semua transaksi pembayaran</p>
            </div>

            <div className="bg-white rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">No. Pesanan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pengguna</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kuantiti</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jumlah</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarikh</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {payments.map((payment) => (
                    <tr key={payment.payment_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm">{payment.order_number}</td>
                      <td className="px-6 py-4 text-sm">{users.find(u => u.user_id === payment.user_id)?.name || payment.user_id}</td>
                      <td className="px-6 py-4">{payment.quantity} kad</td>
                      <td className="px-6 py-4 font-semibold text-[#7678ED]">RM{payment.amount}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          payment.status === 'success' ? 'bg-[#7678ED]/20 text-[#3D348B]' : 
                          payment.status === 'pending' ? 'bg-[#F7B801]/20 text-[#F18701]' : 'bg-red-100 text-red-700'
                        }`}>
                          {payment.status === 'success' ? 'Berjaya' : payment.status === 'pending' ? 'Menunggu' : 'Gagal'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(payment.created_at)}</td>
                    </tr>
                  ))}
                  {payments.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">Tiada rekod pembayaran</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Landing Setup Tab */}
          <TabsContent value="landing" className="space-y-6">
            <div>
              <h2 className="text-3xl font-playfair font-bold text-gray-900">Landing Page Setup</h2>
              <p className="text-gray-600 mt-1">Urus kandungan dan SEO landing page</p>
            </div>

            <div className="grid gap-6">
              {/* YouTube Video */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    Video YouTube
                  </CardTitle>
                  <CardDescription>Video yang dipaparkan di hero section landing page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>YouTube Video ID atau URL</Label>
                    <Input
                      value={landingSettings.youtube_video_id || ''}
                      onChange={(e) => setLandingSettings({ ...landingSettings, youtube_video_id: e.target.value })}
                      placeholder="Contoh: dQw4w9WgXcQ atau https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                    />
                    <p className="text-xs text-gray-500">
                      Masukkan Video ID sahaja (cth: dQw4w9WgXcQ) atau paste full URL YouTube
                    </p>
                  </div>

                  {/* Video Thumbnail Upload */}
                  <div className="space-y-2">
                    <Label>Gambar Thumbnail Video</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Gambar yang dipaparkan sebelum video dimainkan. Jika tidak ditetapkan, gambar default akan digunakan.
                    </p>
                    <div className="flex items-start gap-4">
                      {landingSettings.video_thumbnail ? (
                        <div className="relative w-40 h-24 rounded-lg overflow-hidden border">
                          <img 
                            src={landingSettings.video_thumbnail} 
                            alt="Video thumbnail" 
                            className="w-full h-full object-cover"
                          />
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute top-1 right-1 w-6 h-6"
                            onClick={() => setLandingSettings({ ...landingSettings, video_thumbnail: '' })}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="w-40 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <input
                          type="file"
                          ref={videoThumbnailRef}
                          onChange={handleVideoThumbnailUpload}
                          accept="image/*"
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          onClick={() => videoThumbnailRef.current?.click()}
                          disabled={uploadingVideoThumbnail}
                          data-testid="upload-video-thumbnail-btn"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingVideoThumbnail ? 'Memuat naik...' : 'Muat Naik Gambar'}
                        </Button>
                        <p className="text-xs text-gray-400 mt-1">Saiz disarankan: 1280x720px (16:9)</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Preview */}
                  {landingSettings.youtube_video_id && (
                    <div className="mt-4">
                      <Label className="text-gray-500 text-sm">Preview Video</Label>
                      <div className="mt-2 aspect-video bg-black rounded-lg overflow-hidden max-w-md">
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${landingSettings.youtube_video_id.includes('youtube.com') || landingSettings.youtube_video_id.includes('youtu.be') ? '' : landingSettings.youtube_video_id}`}
                          title="YouTube Preview"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* SEO Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>SEO Settings</CardTitle>
                  <CardDescription>Tetapan Search Engine Optimization untuk landing page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Meta Title</Label>
                    <Input
                      value={landingSettings.seo_title || ''}
                      onChange={(e) => setLandingSettings({ ...landingSettings, seo_title: e.target.value })}
                      placeholder="Kawkad - Kad Kahwin Digital Malaysia"
                    />
                    <p className="text-xs text-gray-500">Title yang dipaparkan di tab browser dan hasil carian Google</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Meta Description</Label>
                    <Textarea
                      value={landingSettings.seo_description || ''}
                      onChange={(e) => setLandingSettings({ ...landingSettings, seo_description: e.target.value })}
                      placeholder="Cipta kad kahwin digital yang cantik dan interaktif. Mudah, pantas dan mampu milik."
                      rows={3}
                    />
                    <p className="text-xs text-gray-500">Penerangan yang dipaparkan di hasil carian Google (max 160 aksara)</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Keywords</Label>
                    <Input
                      value={landingSettings.seo_keywords || ''}
                      onChange={(e) => setLandingSettings({ ...landingSettings, seo_keywords: e.target.value })}
                      placeholder="kad kahwin digital, e-kad kahwin, kad jemputan digital"
                    />
                    <p className="text-xs text-gray-500">Pisahkan dengan koma</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>OG Image URL</Label>
                    <Input
                      value={landingSettings.seo_og_image || ''}
                      onChange={(e) => setLandingSettings({ ...landingSettings, seo_og_image: e.target.value })}
                      placeholder="https://kawkad.com/og-image.jpg"
                    />
                    <p className="text-xs text-gray-500">Imej yang dipaparkan apabila dikongsi di social media</p>
                  </div>
                </CardContent>
              </Card>

              {/* Card SEO Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>SEO Kad Individual</CardTitle>
                  <CardDescription>Tetapan untuk setiap kad kahwin yang dikongsi</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Template Title Kad</Label>
                    <Input
                      value={landingSettings.card_seo_title_template || ''}
                      onChange={(e) => setLandingSettings({ ...landingSettings, card_seo_title_template: e.target.value })}
                      placeholder="{bride} & {groom} | Kad Kahwin Digital"
                    />
                    <p className="text-xs text-gray-500">
                      Gunakan <code className="bg-gray-100 px-1 rounded">{'{bride}'}</code> dan <code className="bg-gray-100 px-1 rounded">{'{groom}'}</code> sebagai placeholder
                    </p>
                  </div>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Nota:</strong> Kad individual akan di-set sebagai <code className="bg-yellow-100 px-1 rounded">noindex</code> untuk elak duplicate content di Google.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button 
                  onClick={handleSaveLandingSettings}
                  disabled={savingLanding}
                  className="bg-[#3D348B] text-white hover:bg-[#7678ED]"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingLanding ? 'Menyimpan...' : 'Simpan Tetapan'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Email/Notification Tab */}
          <TabsContent value="email" className="space-y-6">
            <div>
              <h2 className="text-3xl font-playfair font-bold text-gray-900">Tetapan Email & Notifikasi</h2>
              <p className="text-gray-600 mt-1">Konfigurasi email notifikasi untuk pembayaran gagal</p>
            </div>

            <div className="grid gap-6">
              {/* Resend API Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    Resend API Configuration
                  </CardTitle>
                  <CardDescription>
                    API credentials untuk menghantar email. Dapatkan API key di{' '}
                    <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#3D348B] underline">
                      resend.com/api-keys
                    </a>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Resend API Key {emailSettings.resend_api_key_set && <span className="text-[#3D348B] text-xs">(sudah ditetapkan)</span>}</Label>
                    <div className="flex gap-2">
                      <Input
                        type={showApiKey ? "text" : "password"}
                        value={emailSettings.resend_api_key}
                        onChange={(e) => setEmailSettings({...emailSettings, resend_api_key: e.target.value})}
                        placeholder={emailSettings.resend_api_key_set ? "••••••••" : "re_xxxxxxxxx"}
                        data-testid="resend-api-key-input"
                      />
                      <Button variant="outline" size="icon" onClick={() => setShowApiKey(!showApiKey)}>
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nama Pengirim</Label>
                      <Input
                        value={emailSettings.sender_name}
                        onChange={(e) => setEmailSettings({...emailSettings, sender_name: e.target.value})}
                        placeholder="Kawkad"
                        data-testid="sender-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email Pengirim</Label>
                      <Input
                        value={emailSettings.sender_email}
                        onChange={(e) => setEmailSettings({...emailSettings, sender_email: e.target.value})}
                        placeholder="noreply@kawkad.com"
                        data-testid="sender-email-input"
                      />
                      <p className="text-xs text-gray-500">Guna <code className="bg-gray-100 px-1 rounded">onboarding@resend.dev</code> untuk testing</p>
                    </div>
                  </div>

                  <div className="pt-2">
                    <Button
                      variant="outline"
                      onClick={handleSendTestEmail}
                      disabled={sendingTestEmail || !emailSettings.resend_api_key_set}
                      data-testid="send-test-email-btn"
                    >
                      {sendingTestEmail ? 'Menghantar...' : 'Hantar Test Email'}
                    </Button>
                    {!emailSettings.resend_api_key_set && (
                      <p className="text-xs text-yellow-600 mt-1">Simpan API key terlebih dahulu untuk test</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Notification Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Tetapan Notifikasi</CardTitle>
                  <CardDescription>Bila email perlu dihantar</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Email Segera (Immediate)</Label>
                      <p className="text-xs text-gray-500">Hantar email sejurus selepas pembayaran gagal</p>
                    </div>
                    <Switch
                      checked={emailSettings.enable_immediate_notification}
                      onCheckedChange={(checked) => setEmailSettings({...emailSettings, enable_immediate_notification: checked})}
                      data-testid="immediate-notification-switch"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Email Peringatan (Follow-up)</Label>
                      <p className="text-xs text-gray-500">Hantar peringatan jika user belum retry</p>
                    </div>
                    <Switch
                      checked={emailSettings.enable_followup_notification}
                      onCheckedChange={(checked) => setEmailSettings({...emailSettings, enable_followup_notification: checked})}
                      data-testid="followup-notification-switch"
                    />
                  </div>

                  {emailSettings.enable_followup_notification && (
                    <div className="space-y-2 pl-4 border-l-2 border-gray-200">
                      <Label>Delay Peringatan (jam)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="24"
                        value={emailSettings.followup_delay_hours}
                        onChange={(e) => setEmailSettings({...emailSettings, followup_delay_hours: parseInt(e.target.value) || 2})}
                        className="w-24"
                        data-testid="followup-delay-input"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Immediate Email Template */}
              <Card>
                <CardHeader>
                  <CardTitle>Template Email Segera</CardTitle>
                  <CardDescription>Dihantar sejurus selepas pembayaran gagal</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Input
                      value={emailSettings.immediate_subject}
                      onChange={(e) => setEmailSettings({...emailSettings, immediate_subject: e.target.value})}
                      placeholder="Pembayaran Gagal - Sila Cuba Semula"
                      data-testid="immediate-subject-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kandungan Email (HTML)</Label>
                    <Textarea
                      value={emailSettings.immediate_body}
                      onChange={(e) => setEmailSettings({...emailSettings, immediate_body: e.target.value})}
                      rows={10}
                      className="font-mono text-sm"
                      data-testid="immediate-body-input"
                    />
                    <p className="text-xs text-gray-500">
                      Placeholder yang boleh digunakan: <code className="bg-gray-100 px-1 rounded">{'{name}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{order_number}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{amount}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{quantity}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{retry_url}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{company_name}'}</code>
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Followup Email Template */}
              <Card>
                <CardHeader>
                  <CardTitle>Template Email Peringatan</CardTitle>
                  <CardDescription>Dihantar selepas beberapa jam jika user belum retry</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Input
                      value={emailSettings.followup_subject}
                      onChange={(e) => setEmailSettings({...emailSettings, followup_subject: e.target.value})}
                      placeholder="Peringatan: Pembayaran Anda Belum Selesai"
                      data-testid="followup-subject-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kandungan Email (HTML)</Label>
                    <Textarea
                      value={emailSettings.followup_body}
                      onChange={(e) => setEmailSettings({...emailSettings, followup_body: e.target.value})}
                      rows={10}
                      className="font-mono text-sm"
                      data-testid="followup-body-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Success Email Template */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Template Email Pembayaran Berjaya
                  </CardTitle>
                  <CardDescription>Dihantar apabila pembayaran berjaya</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Label>Aktifkan Email Pembayaran Berjaya</Label>
                      <p className="text-xs text-gray-500">Hantar email apabila pembayaran berjaya</p>
                    </div>
                    <Switch
                      checked={emailSettings.enable_success_notification}
                      onCheckedChange={(checked) => setEmailSettings({...emailSettings, enable_success_notification: checked})}
                      data-testid="success-notification-switch"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Input
                      value={emailSettings.success_subject}
                      onChange={(e) => setEmailSettings({...emailSettings, success_subject: e.target.value})}
                      placeholder="Pembayaran Berjaya - Terima Kasih! 🎉"
                      data-testid="success-subject-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kandungan Email (HTML)</Label>
                    <Textarea
                      value={emailSettings.success_body}
                      onChange={(e) => setEmailSettings({...emailSettings, success_body: e.target.value})}
                      rows={10}
                      className="font-mono text-sm"
                      data-testid="success-body-input"
                    />
                    <p className="text-xs text-gray-500">
                      Placeholder: <code className="bg-gray-100 px-1 rounded">{'{name}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{order_number}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{amount}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{quantity}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{dashboard_url}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{company_name}'}</code>
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Button
                onClick={handleSaveEmailSettings}
                disabled={savingEmail}
                className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full py-6"
                data-testid="save-email-settings-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingEmail ? 'Menyimpan...' : 'Simpan Tetapan Email'}
              </Button>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <div>
              <h2 className="text-3xl font-playfair font-bold text-gray-900">Tetapan Pembayaran</h2>
              <p className="text-gray-600 mt-1">Konfigurasi payment gateway dan harga</p>
            </div>

            {/* Maintenance Mode Card */}
            <Card className="border-orange-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <Settings className="w-5 h-5" />
                  Mod Penyelenggaraan
                </CardTitle>
                <CardDescription>Tutup akses awam semasa penyelenggaraan. Admin masih boleh log masuk.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-800">Status Laman</p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {siteSettings.maintenance_mode
                        ? <span className="text-orange-600 font-semibold">Penyelenggaraan AKTIF — Awam tidak boleh akses</span>
                        : <span className="text-green-600 font-semibold">Laman BUKA — Awam boleh akses</span>}
                    </p>
                  </div>
                  <Switch
                    data-testid="maintenance-mode-toggle"
                    checked={siteSettings.maintenance_mode}
                    onCheckedChange={(checked) => setSiteSettings({ ...siteSettings, maintenance_mode: checked })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mesej Penyelenggaraan</Label>
                  <Input
                    data-testid="maintenance-message-input"
                    value={siteSettings.maintenance_message}
                    onChange={(e) => setSiteSettings({ ...siteSettings, maintenance_message: e.target.value })}
                    placeholder="Laman web sedang dalam penyelenggaraan..."
                  />
                </div>
                <Button
                  data-testid="save-site-settings-btn"
                  onClick={handleSaveSiteSettings}
                  disabled={savingSiteSettings}
                  className="w-full bg-orange-600 text-white hover:bg-orange-700 rounded-full"
                >
                  {savingSiteSettings ? 'Menyimpan...' : 'Simpan Tetapan Laman'}
                </Button>
              </CardContent>
            </Card>

            <div className="grid gap-6">
              {/* Bayarcash Credentials */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Bayarcash Credentials
                  </CardTitle>
                  <CardDescription>API credentials untuk payment gateway (encrypted)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Mode</Label>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${paymentSettings.use_sandbox ? 'text-yellow-600 font-medium' : 'text-gray-400'}`}>Sandbox</span>
                      <Switch
                        checked={!paymentSettings.use_sandbox}
                        onCheckedChange={(checked) => setPaymentSettings({...paymentSettings, use_sandbox: !checked})}
                      />
                      <span className={`text-sm ${!paymentSettings.use_sandbox ? 'text-[#3D348B] font-medium' : 'text-gray-400'}`}>Production</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Portal Key {paymentSettings.bayarcash_portal_key_set && <span className="text-[#3D348B] text-xs">(sudah ditetapkan)</span>}</Label>
                    <div className="flex gap-2">
                      <Input
                        type={showCredentials ? "text" : "password"}
                        value={paymentSettings.bayarcash_portal_key}
                        onChange={(e) => setPaymentSettings({...paymentSettings, bayarcash_portal_key: e.target.value})}
                        placeholder={paymentSettings.bayarcash_portal_key_set ? "••••••••" : "Masukkan Portal Key"}
                      />
                      <Button variant="outline" size="icon" onClick={() => setShowCredentials(!showCredentials)}>
                        {showCredentials ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Personal Access Token (PAT) {paymentSettings.bayarcash_pat_set && <span className="text-[#3D348B] text-xs">(sudah ditetapkan)</span>}</Label>
                    <Input
                      type={showCredentials ? "text" : "password"}
                      value={paymentSettings.bayarcash_pat}
                      onChange={(e) => setPaymentSettings({...paymentSettings, bayarcash_pat: e.target.value})}
                      placeholder={paymentSettings.bayarcash_pat_set ? "••••••••" : "Masukkan PAT"}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Pricing Packages */}
              <Card>
                <CardHeader>
                  <CardTitle>Pakej Harga</CardTitle>
                  <CardDescription>Tetapkan harga untuk setiap pakej kad</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {paymentSettings.pricing_packages.map((pkg, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs">Kuantiti</Label>
                          <Input
                            type="number"
                            min="1"
                            value={pkg.quantity}
                            onChange={(e) => updatePricingPackage(idx, 'quantity', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Harga (RM)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={pkg.price}
                            onChange={(e) => updatePricingPackage(idx, 'price', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Label</Label>
                          <Input
                            value={pkg.label}
                            onChange={(e) => updatePricingPackage(idx, 'label', e.target.value)}
                          />
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removePricingPackage(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" onClick={addPricingPackage} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Tambah Pakej
                  </Button>
                </CardContent>
              </Card>

              {/* Invoice Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Maklumat Invoice</CardTitle>
                  <CardDescription>Maklumat syarikat untuk resit pembayaran</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nama Syarikat</Label>
                    <Input
                      value={paymentSettings.invoice_company_name}
                      onChange={(e) => setPaymentSettings({...paymentSettings, invoice_company_name: e.target.value})}
                      placeholder="Ritornello Kawkad Studio"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Alamat</Label>
                    <Textarea
                      value={paymentSettings.invoice_address}
                      onChange={(e) => setPaymentSettings({...paymentSettings, invoice_address: e.target.value})}
                      placeholder="Alamat syarikat..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>No. Telefon</Label>
                      <Input
                        value={paymentSettings.invoice_phone}
                        onChange={(e) => setPaymentSettings({...paymentSettings, invoice_phone: e.target.value})}
                        placeholder="60123456789"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={paymentSettings.invoice_email}
                        onChange={(e) => setPaymentSettings({...paymentSettings, invoice_email: e.target.value})}
                        placeholder="info@syarikat.com"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button
                onClick={handleSavePaymentSettings}
                disabled={savingSettings}
                className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full py-6"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingSettings ? 'Menyimpan...' : 'Simpan Tetapan'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Pengguna</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nama</Label>
              <Input value={editUserForm.name} onChange={(e) => setEditUserForm({...editUserForm, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={editUserForm.email} onChange={(e) => setEditUserForm({...editUserForm, email: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>No. Telefon</Label>
              <Input value={editUserForm.phone} onChange={(e) => setEditUserForm({...editUserForm, phone: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>No. WhatsApp</Label>
              <Input data-testid="edit-user-whatsapp-input" value={editUserForm.whatsapp} onChange={(e) => setEditUserForm({...editUserForm, whatsapp: e.target.value})} placeholder="60123456789" />
            </div>
            <div className="space-y-2">
              <Label>Kredit Kad</Label>
              <Input type="number" min="0" value={editUserForm.card_credits} onChange={(e) => setEditUserForm({...editUserForm, card_credits: parseInt(e.target.value) || 0})} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Status Gantung</Label>
              <Switch checked={editUserForm.suspended} onCheckedChange={(checked) => setEditUserForm({...editUserForm, suspended: checked})} />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editUserForm.role} onValueChange={(value) => setEditUserForm({...editUserForm, role: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleUpdateUser} disabled={processing} className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full">
              {processing ? 'Menyimpan...' : 'Simpan Perubahan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteUserDialog} onOpenChange={setDeleteUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Padam Pengguna
            </DialogTitle>
            <DialogDescription>
              Pengguna "{selectedUser?.name}" mempunyai {getUserCardCount(selectedUser?.user_id)} kad.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input type="radio" name="deleteOption" checked={deleteOption === 'delete'} onChange={() => setDeleteOption('delete')} />
                <div>
                  <p className="font-medium">Padam semua kad</p>
                  <p className="text-sm text-gray-500">Kad pengguna akan dipadamkan bersama</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input type="radio" name="deleteOption" checked={deleteOption === 'transfer'} onChange={() => setDeleteOption('transfer')} />
                <div>
                  <p className="font-medium">Pindahkan kad ke pengguna lain</p>
                  <p className="text-sm text-gray-500">Pilih pengguna untuk menerima kad</p>
                </div>
              </label>
            </div>
            {deleteOption === 'transfer' && (
              <div className="space-y-2">
                <Label>Pindahkan ke:</Label>
                <Select value={transferToUserId} onValueChange={setTransferToUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih pengguna" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.user_id !== selectedUser?.user_id).map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUserDialog(false)}>Batal</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser} 
              disabled={processing || (deleteOption === 'transfer' && !transferToUserId)}
            >
              {processing ? 'Memproses...' : 'Padam Pengguna'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog} onOpenChange={setResetPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Kata Laluan</DialogTitle>
            <DialogDescription>Tetapkan kata laluan baharu untuk {selectedUser?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Kata Laluan Baharu</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Minimum 6 aksara" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleResetPassword} disabled={processing || newPassword.length < 6} className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full">
              {processing ? 'Menyimpan...' : 'Reset Kata Laluan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Credits Dialog */}
      <Dialog open={addCreditsDialog} onOpenChange={setAddCreditsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Tambah Kredit</DialogTitle>
            <DialogDescription>Tambah kredit kad untuk {selectedUser?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Kredit semasa</p>
              <p className="text-3xl font-bold text-[#7678ED]">{selectedUser?.card_credits || 0}</p>
            </div>
            <div className="space-y-2">
              <Label>Bilangan Kredit untuk Ditambah</Label>
              <Input type="number" min="1" value={creditsToAdd} onChange={(e) => setCreditsToAdd(parseInt(e.target.value) || 1)} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleAddCredits} disabled={processing} className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full">
              {processing ? 'Menambah...' : `Tambah ${creditsToAdd} Kredit`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={editTemplateDialog} onOpenChange={setEditTemplateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl">Edit Templat</DialogTitle>
            <DialogDescription>Kemaskini maklumat templat</DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Nama Templat</Label>
                <Input
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  placeholder="Templat Klasik"
                />
              </div>
              <div className="space-y-2">
                <Label>Penerangan</Label>
                <Textarea
                  value={editingTemplate.description}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                  placeholder="Penerangan ringkas templat..."
                />
              </div>
              <div className="space-y-2">
                <Label>Thumbnail (Saiz IG Story 9:16)</Label>
                <div className="flex gap-4 items-start">
                  {/* Preview */}
                  <div className="w-24 h-[170px] bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border-2 border-dashed border-gray-300">
                    {editingTemplate.preview_image ? (
                      <img src={editingTemplate.preview_image} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <ImageIcon className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      type="file"
                      id="edit-thumbnail-input"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleThumbnailUpload(e, true)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('edit-thumbnail-input')?.click()}
                      disabled={uploadingThumbnail}
                      className="w-full"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingThumbnail ? 'Memuat naik...' : 'Muat Naik Thumbnail'}
                    </Button>
                    <p className="text-xs text-gray-500">Imej akan di-crop ke 9:16. Max 2MB.</p>
                    <div className="text-xs text-gray-400">atau masukkan URL:</div>
                    <Input
                      value={editingTemplate.preview_image}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, preview_image: e.target.value })}
                      placeholder="https://..."
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditTemplateDialog(false)}>
                  Batal
                </Button>
                <Button 
                  onClick={handleUpdateTemplate} 
                  disabled={processing}
                  className="bg-[#3D348B] text-white hover:bg-[#7678ED]"
                >
                  {processing ? 'Menyimpan...' : 'Simpan Perubahan'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
